import api from '../api/client';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Descriptions, Table, Button, Space, Tag, Statistic, Row, Col, Typography, Divider } from 'antd';
import { ShoppingCartOutlined, MedicineBoxOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import moment from 'moment';
import config from '../config';

const { Title, Text } = Typography;

const ItemDetailsPage = () => {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;

    useEffect(() => {
        fetchItemSummary();
        fetchHistory();
    }, [id]);

    const fetchItemSummary = async () => {
        try {
            const res = await api.get(`/items/${id}/summary`);
            setItem(res.data);
        } catch (err) {
            console.error('Error fetching item summary:', err);
        }
    };

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/items/${id}/history`);
            setHistory(res.data);
        } catch (err) {
            console.error('Error fetching item history:', err);
        } finally {
            setLoading(false);
        }
    };

    const historyColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            render: (date) => moment(date).format('DD/MM/YYYY')
        },
        {
            title: 'Type',
            dataIndex: 'type',
            render: (type) => (
                <Tag color={type === 'new_stock' ? 'green' : type === 'restock' ? 'orange' : 'blue'}>
                    {type === 'restock' ? 'Restock' : type === 'new_stock' ? 'New Stock' : 'Consumption'}
                </Tag>
            )
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            render: (qty) => qty
        },
        {
            title: 'Unit Cost (KES)',
            dataIndex: 'unit_cost',
            render: (cost) => cost?.toFixed(2)
        },
        {
            title: 'Total (KES)',
            dataIndex: 'total_cost',
            render: (total) => total?.toFixed(2)
        },
        {
            title: 'Reference',
            dataIndex: 'stakeholder_name',
            render: (name, record) =>
                record.type === 'restock' ? (
                    <div>
                        <div>{record?.invoice_number}</div>
                        <div>{name}</div>
                    </div>
                ) : (
                    name
                )
        },
        {
            title: 'Voided',
            dataIndex: 'voided',
            render: (voided) => (voided ? 'Yes' : 'No')
        },
        {
            title: 'Voided Date',
            dataIndex: 'voided_at',
            render: (date) => (date ? moment(date).format('DD/MM/YYYY') : null)
        }
    ];

    if (!item) return <div>Loading...</div>;

    return (
        <div>
            <Card>
                <Row gutter={16}>
                    <Col span={16}>
                        <Title level={3}>{item.name}</Title>
                        <Text type="secondary">
                            {item.type} - {item.category}
                        </Text>
                    </Col>
                    <Col span={8} style={{ textAlign: 'right' }}>
                        <Space>
                            <Link to={`/inventory/add?item=${item.id}`}>
                                <Button type="primary" icon={<ShoppingCartOutlined />}>
                                    Restock
                                </Button>
                            </Link>
                            <Link to={`/inventory/consumption?item=${item.id}`}>
                                <Button icon={<MedicineBoxOutlined />}>Consume</Button>
                            </Link>{' '}
                            <Button icon={<ArrowLeftOutlined />} onClick={() => window.history.back()}>
                                Back
                            </Button>
                        </Space>
                    </Col>
                </Row>
                <Divider />
                <Row gutter={16}>
                    <Col span={6}>
                        <Statistic title="Current Stock" value={item.current_stock} suffix={item.unit} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Initial Stock" value={item.initial_stock} suffix={item.unit} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Total Restocked" value={item.total_restocked} suffix={item.unit} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Total Consumed" value={item.total_consumed} suffix={item.unit} />
                    </Col>
                </Row>
                <Divider />
                <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={12}>
                        {/* <Card title="Restock Value" size="small"> */}
                        <Statistic title="Restock Value" value={item.total_restock_cost} precision={2} prefix="KES" />
                        {/* </Card> */}
                    </Col>
                    <Col span={12}>
                        {/* <Card title="Consumed Value" size="small"> */}
                        <Statistic title="Consumed Value" value={item.total_consumed_value} precision={2} prefix="KES" />
                        {/* </Card> */}
                    </Col>
                </Row>
                <Divider />
                <Title level={4}>Transaction History</Title>
                <Table
                    size="small"
                    width="100%"
                    scroll={{ x: 'max-content' }}
                    columns={historyColumns}
                    dataSource={history}
                    rowKey={(record, idx) => idx}
                    loading={loading}
                    pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100', '200', '500'] }}
                />
            </Card>
        </div>
    );
};

export default ItemDetailsPage;
